import { useLocation } from "react-router";
import DashboardIcon from "../../assets/svg/SideNav/DashboardIcon";
import SubmittedTestOrdersIcon from "../../assets/svg/SideNav/SubmittedTestOrdersIcon";
import React, { Fragment, useContext, useRef, useEffect } from "react";
import SidebarItemProps from "../../services/models/sideBarItemProps.model";
import { NavLink } from "react-router-dom";
import SettingsIcon from "../../assets/svg/SideNav/SettingsIcon";
import PDFIcon from "../../assets/svg/SideNav/PDFIcon";
import ApprovalsIcon from "../../assets/svg/SideNav/ApprovalsIcon";
import PatientsIcon from "../../assets/svg/SideNav/PatientsIcon";
import ShipmentIcon from "../../assets/svg/SideNav/ShipmentsIcon";
import useProfile from "../../services/utils/custom hooks/useProfile";
import SideNavLinkLoadingIndicator from "./LoadingIndicators/SideNavLinkLoadingIndicator";
import useDashboardStatus from "../../services/utils/custom hooks/useDashboardStatus";
import { useAdminContext } from "../../services/context/TourPopupContext";
import useFeatureFlags from "../../services/utils/custom hooks/Feature Flagging/useFeatureFlags";
import dataConstants from "../../constants/dataConstants";
import { twMerge } from "tailwind-merge";

function ApprovalsCountBadge({
  unReadCount,
}: {
  unReadCount: any;
}): JSX.Element | null {
  if (unReadCount === 0) return null;
  return (
    <div
      className={`bg-[#FE4F4F] ${unReadCount > 99 ? "h-[18px] w-[18px]" : "h-[15px] w-[15px]"
        } rounded-full absolute   truncate  xs:top-[83px] md:top-0 ml-2 lg:ml-0 lg:top-[118px] lg:left-14`}
    >
      <p
        className={`text-white ${unReadCount > 99 ? "text-[8px] mt-1" : "text-xs"
          } text-center`}
      >
        {unReadCount > 100 ? (
          <span>
            100<sup>+</sup>
          </span>
        ) : (
          unReadCount
        )}
      </p>
    </div>
  );
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  label,
  icon,
  url,
  disabled,
  className = "",
  toggleMobileMenu,
}) => {
  const location = useLocation();

  const { isTourActive } = useAdminContext();

  return (
    <NavLink
      to={disabled ? "#" : url}
      onClick={() => {
        if (toggleMobileMenu) toggleMobileMenu();
      }}
      className={`sideBarItem  ${label === "Test Orders" &&
        location?.pathname.includes("/access/online-order") &&
        "sideBarItemActive"
        } ${label === "Shipments" &&
        location?.pathname?.includes("/access/schedule-pickup") &&
        "sideBarItemActive"
        } ${className}`}
      activeClassName={
        isTourActive && label === "Patients"
          ? "cursor-not-allowed"
          : "sideBarItemActive"
      }
      id={url === "/access/patients" ? "step3" : undefined}
    >
      {icon}
      {label}
    </NavLink>
  );
};

function Sidebar({
  containerClassName,
  toggleMobileView,
  closeMobileMenu,
  isOpen,
}: Readonly<{
  containerClassName?: string;
  toggleMobileView?: () => void;
  closeMobileMenu?: () => void;
  isOpen?: boolean;
}>): JSX.Element {
  const sidebarRef = useRef<HTMLElement>(null);
  const { isSectionActive } = useFeatureFlags();
  const { data: profile, isLoading: fetchingProfile } = useProfile();
  const roles = profile?.user?.roles ?? [];

  const { isLoading, data } = useDashboardStatus();
  const isPLUUser = roles.includes("PathLab User") && roles.length === 1;
  const isCOA =
    roles.includes("Collaborative Order Agreement") && roles.length === 1;
  const className = twMerge(
    "md:block border-r border-t lg:border-t-0 lg:flex lg:flex-col lg:h-[calc(100vh_-_57px)] justify-between w-full order-last lg:order-first relative",
    containerClassName
  );

  const PLUforbiddenLinks = [
    "/access/approvals",
    "/access/documents",
    "/access/patients",
    "/profile",
  ];
  const COAForbiddenLinks = ["/access/approvals"];
  const location = useLocation();
  const { isTourActive } = useAdminContext();

  // Click outside handler - only active when menu is open
  useEffect(() => {
    // Only run if menu is open and closeMobileMenu function exists
    if (!isOpen || !closeMobileMenu) return;

    // Check if we're in mobile view
    const isMobile = window.innerWidth < 1024;
    if (!isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Don't close if clicking on the sidebar itself
      if (sidebarRef.current && sidebarRef.current.contains(target)) {
        return;
      }

      // Don't close if clicking on hamburger button or its children
      const hamburgerButton = target.closest('button[type="button"]');
      if (hamburgerButton) {
        return;
      }

      // Close the menu
      closeMobileMenu();
    };

    // Add small delay to ensure hamburger click completes first
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 50);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, closeMobileMenu]);

  const thirdMenuItem = {
    label: "Settings",
    icon: (
      <SettingsIcon
        className="ml-3 self-center items-center h-full justify-center"
        height={20}
        width={30}
      />
    ),
    key: dataConstants.FeatureFlagSectionKeys.SETTINGS,
    url: "/access/settings",
  };

  const menuItems: SidebarItemProps[] = [
    {
      label: "Dashboard",
      key: dataConstants.FeatureFlagSectionKeys.DASHBOARD,
      icon: (
        <DashboardIcon
          stroke={
            location?.pathname === "/access/dashboard" ? "white" : "#172B4D"
          }
          className="ml-3 self-center items-center h-full justify-center"
          height={20}
          width={30}
        />
      ),
      url: "/access/dashboard",
    },
    {
      label: "Test Orders",
      key: dataConstants.FeatureFlagSectionKeys.TEST_ORDERS,
      icon: (
        <SubmittedTestOrdersIcon
          stroke={
            location?.pathname.includes("/access/test-orders") ||
              location?.pathname?.includes("/access/online-order")
              ? "white"
              : "#172B4D"
          }
          className="ml-3 self-center items-center h-full justify-center"
          fill="white"
          height={20}
          width={30}
        />
      ),
      url: "/access/test-orders",
    },
    {
      label: "Approvals",
      key: dataConstants.FeatureFlagSectionKeys.APPROVALS,
      icon: (
        <>
          <ApprovalsIcon
            stroke={
              location?.pathname.includes("/access/approvals")
                ? "white"
                : "#172B4D"
            }
            className="ml-3 self-center items-center h-full justify-center"
            height={20}
            width={30}
          />

          <ApprovalsCountBadge unReadCount={data?.data.pendingApproval} />
        </>
      ),
      url: "/access/approvals",
    },
    {
      label: "Patients",
      key: dataConstants.FeatureFlagSectionKeys.PATIENTS,
      icon: (
        <PatientsIcon
          stroke={
            location?.pathname.includes("/access/patients")
              ? "white"
              : "#172B4D"
          }
          className={`ml-3 self-center items-center h-full justify-center`}
          height={20}
          width={30}
        />
      ),
      url: "/access/patients",
      disabled: isTourActive,
    },
    {
      label: "Shipments",
      key: dataConstants.FeatureFlagSectionKeys.SHIPMENTS,
      icon: (
        <ShipmentIcon
          stroke={
            location?.pathname.includes("/access/shipments") ||
              location?.pathname?.includes("/access/schedule-pickup")
              ? "white"
              : "#172B4D"
          }
          className="ml-3 self-center items-center h-full justify-center"
          height={20}
          width={30}
        />
      ),
      url: "/access/shipments",
    },
    {
      label: "Documents",
      key: dataConstants.FeatureFlagSectionKeys.DOCUMENTS,
      icon: (
        <PDFIcon
          stroke={
            location?.pathname.includes("/access/documents")
              ? "white"
              : "#172B4D"
          }
          className="ml-3 self-center items-center h-full justify-center"
          height={20}
          width={30}
        />
      ),
      url: "/access/documents",
    },
    thirdMenuItem,
  ];

  return (
    <nav ref={sidebarRef} className={className}>
      <div className="md:flex lg:flex-col w-full justify-between">
        {menuItems
          .filter((item) => {
            if (isPLUUser)
              return PLUforbiddenLinks.includes(item.url) === false;
            if (isCOA) return COAForbiddenLinks.includes(item.url) === false;
            return isSectionActive(item.key);
          })
          .map((item, index) => {
            if (fetchingProfile)
              return <SideNavLinkLoadingIndicator key={item.key} />;
            return (
              <SidebarItem
                {...item}
                toggleMobileMenu={toggleMobileView}
                className={
                  item.key === dataConstants.FeatureFlagSectionKeys.SETTINGS
                    ? `lg:hidden`
                    : ""
                }
              />
            );
          })}
      </div>

      <div className=" md:lg:flex-col hidden lg:flex">
        {fetchingProfile ? (
          <SideNavLinkLoadingIndicator
            key={dataConstants.FeatureFlagSectionKeys.USER_PROFILE}
          />
        ) : (
          <Fragment>
            {isSectionActive(thirdMenuItem.key) && (
              <SidebarItem {...thirdMenuItem} />
            )}
          </Fragment>
        )}
      </div>
    </nav>
  );
}

export default Sidebar;